import { useMemo } from 'react'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import useOffer from 'hooks/Offers/useOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import { getLowestPriceMinDurationPackage } from 'lib/offer/getLowestPricePackage'
import { isFlashHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import { isRegionFilterBypassedForOffer } from 'selectors/marketingSelectors'

interface HotelSearchTileMinStayPricing {
  shouldUseAgentHubFlashOfferMinStayPricing: boolean
  minimumDurationPackage?: App.HotelPackage
  shouldUsePublicOfferMinStayPricing: boolean
  publicOfferAlreadyFetched: boolean
  publicOfferMinDurationPackage?: App.HotelPackage
  applyPublicOfferMinStayPricing: boolean
  usesMinStayPricing: boolean
}

function useHotelSearchTileMinStayPricing(
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary,
  filters: App.OfferListFilters | undefined,
): HotelSearchTileMinStayPricing {
  const hasSearchedStayDuration = !!((filters?.checkIn && filters?.checkOut) || filters?.flexibleNights)
  const minimumDurationPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!isFlashHotelOffer(offer) || !offer.minDuration) {
      return undefined
    }

    return getLowestPriceMinDurationPackage(offer, offer.packages, offer.minDuration)
  }, [offer])
  const shouldUseAgentHubFlashOfferMinStayPricing = isAgentHubEnabled && isFlashHotelOffer(offer) && !!minimumDurationPackage && !hasSearchedStayDuration

  // Anytime search (no dates/flexible nights) with region filter bypassed can't rely on the
  // search stream's single lowest price. Source the full offer from public-offer instead, so we
  // can price from the lowest-price package at the offer's minimum length of stay — selected the
  // same way the offer page selects it, so the tile can't lead with a price the PDP won't honour.
  const isAnytimeSearch = !filters?.checkIn && !filters?.checkOut && !filters?.flexibleNights
  const bypassRegionFilter = useAppSelector(state => isRegionFilterBypassedForOffer(state, offer.id))
  const shouldUsePublicOfferMinStayPricing = isAnytimeSearch
    && bypassRegionFilter
    && isLEHotel(offer)
  const publicOfferAlreadyFetched = useAppSelector((state) => !!state.offer.offersBypassRegion?.[offer.id])
  const [publicOffer] = useOffer<App.HotelOffer>(offer.id, {
    disabled: !shouldUsePublicOfferMinStayPricing,
    bypassRegionFilter: true,
  })
  const publicOfferMinDurationPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (!shouldUsePublicOfferMinStayPricing || !publicOffer?.minDuration || !publicOffer.packages?.length) {
      return undefined
    }

    return getLowestPriceMinDurationPackage(publicOffer, publicOffer.packages, publicOffer.minDuration)
  }, [shouldUsePublicOfferMinStayPricing, publicOffer])
  // Guard with publicOfferAlreadyFetched: publicOffer is sourced from getAllOffers which includes
  // the search-stream summary (which has packages). Without this guard, applyPublicOfferMinStayPricing
  // becomes true immediately from the summary's packages before the bypass fetch completes.
  const applyPublicOfferMinStayPricing = shouldUsePublicOfferMinStayPricing && publicOfferAlreadyFetched && !!publicOfferMinDurationPackage

  return useMemo<HotelSearchTileMinStayPricing>(() => ({
    shouldUseAgentHubFlashOfferMinStayPricing,
    minimumDurationPackage,
    shouldUsePublicOfferMinStayPricing,
    publicOfferAlreadyFetched,
    publicOfferMinDurationPackage,
    applyPublicOfferMinStayPricing,
    usesMinStayPricing: shouldUseAgentHubFlashOfferMinStayPricing || applyPublicOfferMinStayPricing,
  }), [
    shouldUseAgentHubFlashOfferMinStayPricing,
    minimumDurationPackage,
    shouldUsePublicOfferMinStayPricing,
    publicOfferAlreadyFetched,
    publicOfferMinDurationPackage,
    applyPublicOfferMinStayPricing,
  ])
}

export default useHotelSearchTileMinStayPricing
